import React, { useState, useEffect } from 'react';
import type { User, AuthView } from './types';
import { OnboardingModal } from './components/OnboardingModal';
import { ToastProvider } from './components/Toast';
import { ErrorBoundary } from './components/ErrorBoundary';

// Lazy Loaded Routes
const LandingPage = React.lazy(() => import('./components/LandingPage').then(m => ({ default: m.LandingPage })));
const LoginView = React.lazy(() => import('./components/LoginView').then(m => ({ default: m.LoginView })));
const ClientPortal = React.lazy(() => import('./components/ClientPortal').then(m => ({ default: m.ClientPortal })));
const AdminPanel = React.lazy(() => import('./components/AdminPanel').then(m => ({ default: m.AdminPanel })));
// @ts-ignore
const EditorialStudio = React.lazy(() => import('./components/EditorialStudio'));
const HivaRealtime = React.lazy(() => import('./components/HivaRealtime').then(m => ({ default: m.HivaRealtime })));
const HivaRealtimeGuide = React.lazy(() => import('./components/HivaRealtimeGuide').then(m => ({ default: m.HivaRealtimeGuide })));
const HivaRealtimeInstallations = React.lazy(() => import('./components/HivaRealtimeInstallations').then(m => ({ default: m.HivaRealtimeInstallations })));
const HivaRealtimeMarketing = React.lazy(() => import('./components/HivaRealtimeMarketing').then(m => ({ default: m.HivaRealtimeMarketing })));
const HivaRealtimeLaunch = React.lazy(() => import('./components/HivaRealtimeLaunch').then(m => ({ default: m.HivaRealtimeLaunch })));
const HivaRealtimePrivacy = React.lazy(() => import('./components/HivaRealtimePrivacy').then(m => ({ default: m.HivaRealtimePrivacy })));
const HivaRealtimeAccount = React.lazy(() => import('./components/HivaRealtimeAccount').then(m => ({ default: m.HivaRealtimeAccount })));
const HivaRealtimeTestStore = React.lazy(() => import('./components/HivaRealtimeTestStore').then(m => ({ default: m.HivaRealtimeTestStore })));
const HivaHome = React.lazy(() => import('./components/HivaHome').then(m => ({ default: m.HivaHome })));
const BrandLibrary = React.lazy(() => import('./components/BrandLibrary').then(m => ({ default: m.BrandLibrary })));
const ShoppingPanel = React.lazy(() => import('./components/ShoppingPanel').then(m => ({ default: m.ShoppingPanel })));
import { supabase } from './services/supabase';
import { initPostHog, analytics } from './services/analytics';
import { resolveBrandFromDomain, loadUserBrand } from './services/brand';
import type { BrandTheme } from './services/brand';
import { applyBrandTheme } from './services/applyBrandTheme';

const App: React.FC = () => {
    // ===== ALL HOOKS DECLARED FIRST (Rules of Hooks) =====
    const [routeHash, setRouteHash] = useState(window.location.hash);
    const [isClientRoute, setIsClientRoute] = useState(
        window.location.hash.startsWith('#/client')
    );
    const [isAdminRoute, setIsAdminRoute] = useState(
        window.location.hash.startsWith('#/admin')
    );
    const [isLibraryRoute, setIsLibraryRoute] = useState(
        window.location.hash.startsWith('#/biblioteca')
    );
    const [isShoppingRoute, setIsShoppingRoute] = useState(
        window.location.hash.startsWith('#/shopping')
    );
    const [isRealtimeRoute, setIsRealtimeRoute] = useState(
        window.location.hash.startsWith('#/realtime') && !window.location.hash.startsWith('#/realtime/instalacao') && !window.location.hash.startsWith('#/realtime/embed') && !window.location.hash.startsWith('#/realtime/integracao') && !window.location.hash.startsWith('#/realtime/lancamento') && !window.location.hash.startsWith('#/realtime/privacidade') && !window.location.hash.startsWith('#/realtime/conta') && !window.location.hash.startsWith('#/realtime/teste')
    );
    const [isRealtimeGuideRoute, setIsRealtimeGuideRoute] = useState(
        window.location.hash.startsWith('#/realtime/instalacao')
    );
    const [isEmbeddedRealtime, setIsEmbeddedRealtime] = useState(
        window.location.hash.startsWith('#/realtime') && new URLSearchParams(window.location.hash.split('?')[1] || '').has('launch')
    );
    const [isRealtimeInstallRoute, setIsRealtimeInstallRoute] = useState(
        window.location.hash.startsWith('#/realtime/embed')
    );
    const [isRealtimeMarketingRoute, setIsRealtimeMarketingRoute] = useState(
        window.location.hash.startsWith('#/realtime/integracao')
    );
    const [isRealtimeLaunchRoute, setIsRealtimeLaunchRoute] = useState(
        window.location.hash.startsWith('#/realtime/lancamento')
    );
    const [isRealtimePrivacyRoute, setIsRealtimePrivacyRoute] = useState(
        window.location.hash.startsWith('#/realtime/privacidade')
    );
    const [isRealtimeAccountRoute, setIsRealtimeAccountRoute] = useState(
        window.location.hash.startsWith('#/realtime/conta')
    );
    const [isRealtimeTestRoute, setIsRealtimeTestRoute] = useState(
        window.location.hash.startsWith('#/realtime/teste')
    );
    const [isAccountRoute, setIsAccountRoute] = useState(
        window.location.hash.startsWith('#/conta')
    );
    const [currentUser, setCurrentUser] = useState<User | null>(null);
    const [authView, setAuthView] = useState<AuthView>('landing');
    const [isOnboardingOpen, setIsOnboardingOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [brandTheme, setBrandTheme] = useState<BrandTheme | null>(null);
    const [isAdmin, setIsAdmin] = useState(false);
    const [isShoppingManager, setIsShoppingManager] = useState(false);
    const [isDemoSession, setIsDemoSession] = useState(false);

    const openDemoSession = () => {
        setIsDemoSession(true);
        setIsAdmin(false);
        setIsShoppingManager(false);
        setCurrentUser({
            id: 'public-demo',
            email: 'demo@hiva.ai',
            role: 'user',
            credits: 0,
        });
        setIsLoading(false);
    };


    // Hash routing listener
    useEffect(() => {
        const handleHash = () => {
            setRouteHash(window.location.hash);
            setIsClientRoute(window.location.hash.startsWith('#/client'));
            setIsAdminRoute(window.location.hash.startsWith('#/admin'));
            setIsLibraryRoute(window.location.hash.startsWith('#/biblioteca'));
            setIsShoppingRoute(window.location.hash.startsWith('#/shopping'));
            setIsRealtimeRoute(window.location.hash.startsWith('#/realtime') && !window.location.hash.startsWith('#/realtime/instalacao') && !window.location.hash.startsWith('#/realtime/embed') && !window.location.hash.startsWith('#/realtime/integracao') && !window.location.hash.startsWith('#/realtime/lancamento') && !window.location.hash.startsWith('#/realtime/privacidade') && !window.location.hash.startsWith('#/realtime/conta') && !window.location.hash.startsWith('#/realtime/teste'));
            setIsRealtimeGuideRoute(window.location.hash.startsWith('#/realtime/instalacao'));
            setIsEmbeddedRealtime(window.location.hash.startsWith('#/realtime') && new URLSearchParams(window.location.hash.split('?')[1] || '').has('launch'));
            setIsRealtimeInstallRoute(window.location.hash.startsWith('#/realtime/embed'));
            setIsRealtimeMarketingRoute(window.location.hash.startsWith('#/realtime/integracao'));
            setIsRealtimeLaunchRoute(window.location.hash.startsWith('#/realtime/lancamento'));
            setIsRealtimePrivacyRoute(window.location.hash.startsWith('#/realtime/privacidade'));
            setIsRealtimeAccountRoute(window.location.hash.startsWith('#/realtime/conta'));
            setIsRealtimeTestRoute(window.location.hash.startsWith('#/realtime/teste'));
            setIsAccountRoute(window.location.hash.startsWith('#/conta'));
        };
        window.addEventListener('hashchange', handleHash);
        return () => window.removeEventListener('hashchange', handleHash);
    }, []);

    // Resolve brand from domain on boot (pre-login)
    useEffect(() => {
        resolveBrandFromDomain().then(brand => {
            if (brand) {
                setBrandTheme(brand);
                applyBrandTheme(brand);
            }
        });
    }, []);

    // 1. Session Management
    useEffect(() => {
        initPostHog();

        if (sessionStorage.getItem('hiva_demo_session') === 'true') {
            openDemoSession();
            return;
        }

        if (!supabase) {
            setIsLoading(false);
            return;
        }

        supabase.auth.getSession().then(({ data: { session } }) => {
            if (session?.user) {
                setIsDemoSession(false);
                fetchUserData(session.user.id, session.user.email || '');
            } else {
                setIsLoading(false);
            }
        });

        const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
            if (session?.user) {
                setIsDemoSession(false);
                fetchUserData(session.user.id, session.user.email || '');
            } else {
                setCurrentUser(null);
                setAuthView('landing');
                setIsLoading(false);
            }
        });

        return () => subscription?.unsubscribe();
    }, []);

    // 2. Data Fetching — carrega marca e saldo da marca (B2B)
    const fetchUserData = async (userId: string, email: string) => {
        if (!supabase) return;

        try {
            // Carrega dados completos da marca (com saldo e role)
            const brand = await loadUserBrand();
            if (brand) {
                setBrandTheme(brand);
                applyBrandTheme(brand);
            }

            // Verificações independentes em paralelo: evita espera em série no login.
            const [adminResponse, managerResponse] = await Promise.all([
                supabase.rpc('is_superadmin'),
                supabase.rpc('is_shopping_manager'),
            ]);
            const adminCheck = adminResponse.data;
            setIsAdmin(!!adminCheck);

            // Check shopping manager status
            const managerCheck = managerResponse.data;
            const isMgr = !!managerCheck;
            setIsShoppingManager(isMgr);

            // Redirect shopping managers automatically to #/shopping
            if (isMgr && !window.location.hash.startsWith('#/shopping')) {
                window.location.hash = '#/shopping';
                setIsShoppingRoute(true);
            }

            setCurrentUser({
                id: userId,
                email: email,
                role: isMgr ? 'shopping_manager' : ((brand?.userRole as User['role']) || 'member'),
                credits: brand?.creditsBalance || 0
            });

            // A primeira tela é a escolha Studio, Realtime ou Conta. O
            // tutorial legado de créditos não pode interceptar essa entrada.
            setIsOnboardingOpen(false);
            analytics.studioEnter(false);
            analytics.setUserProperties(userId, {
                email: email,
                credits: brand?.creditsBalance || 0,
                has_access: true
            });
        } catch (err) {
            console.error('Error fetching user data:', err);
        } finally {
            setIsLoading(false);
        }
    };

    const handleLogin = (user: User) => {
        setIsDemoSession(false);
        window.location.hash = '';
        setCurrentUser(user);
    };

    const handleDemoAccess = () => {
        sessionStorage.setItem('hiva_demo_session', 'true');
        openDemoSession();
        window.location.hash = '';
    };

    const handleLogout = async () => {
        if (isDemoSession) {
            sessionStorage.removeItem('hiva_demo_session');
            setIsDemoSession(false);
            setCurrentUser(null);
            setAuthView('landing');
            return;
        }
        if (supabase) await supabase.auth.signOut();
        setCurrentUser(null);
        setAuthView('landing');
    };

    const navigateToLanding = () => setAuthView('landing');

    // Loader genérico para o Suspense
    const SuspenseLoader = () => (
        <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50 text-gray-900">
            <div className="w-12 h-12 border-4 border-black/20 border-t-black rounded-full animate-spin mb-4"></div>
        </div>
    );

    const renderAuthView = () => {
        return (
            <React.Suspense fallback={<SuspenseLoader />}>
                {authView === 'login' ? (
                    <LoginView onLogin={handleLogin} onDemoAccess={handleDemoAccess} onBackToLanding={navigateToLanding} />
                ) : (
                    <LandingPage
                        onLoginClick={() => setAuthView('login')}
                        onDemoClick={handleDemoAccess}
                        onRealtimeClick={() => { window.location.hash = '#/realtime/integracao'; }}
                        brandTheme={brandTheme}
                    />
                )}
            </React.Suspense>
        );
    };

    // ===== CONDITIONAL RENDERS (after all hooks) =====

    // Client Portal Route
    if (isClientRoute && currentUser && !isDemoSession) {
        return (
            <ErrorBoundary>
                <React.Suspense fallback={<SuspenseLoader />}>
                    <ClientPortal externalUser={currentUser} />
                </React.Suspense>
            </ErrorBoundary>
        );
    }

    // Admin Panel Route (superadmin only)
    if (isAdminRoute && isAdmin && currentUser) {
        return (
            <ErrorBoundary>
                <ToastProvider>
                    <React.Suspense fallback={<SuspenseLoader />}>
                        <AdminPanel onExit={() => { window.location.hash = ''; }} />
                    </React.Suspense>
                </ToastProvider>
            </ErrorBoundary>
        );
    }

    // Library Route (brand owner/admin only)
    if (isLibraryRoute && currentUser && !isDemoSession) {
        const brandId = (brandTheme as any)?.id || '';
        return (
            <ErrorBoundary>
                <ToastProvider>
                    <React.Suspense fallback={<SuspenseLoader />}>
                        <BrandLibrary
                            brandTheme={brandTheme}
                            brandId={brandId}
                            onBack={() => { window.location.hash = ''; }}
                        />
                    </React.Suspense>
                </ToastProvider>
            </ErrorBoundary>
        );
    }

    // Shopping Manager Route
    if ((isShoppingRoute || isShoppingManager) && currentUser && !isDemoSession) {
        return (
            <ErrorBoundary>
                <ToastProvider>
                    <React.Suspense fallback={<SuspenseLoader />}>
                        <ShoppingPanel onLogout={handleLogout} />
                    </React.Suspense>
                </ToastProvider>
            </ErrorBoundary>
        );
    }

    // Loading
    if (isLoading) {
        return (
            <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50 text-gray-900">
                <div className="w-12 h-12 border-4 border-black/20 border-t-white rounded-full animate-spin mb-4"></div>
                <p className="text-gray-500 font-medium">Carregando Studio...</p>
            </div>
        );
    }

    // Studio (main app)
    return (
        <ErrorBoundary>
            <ToastProvider>
                <div style={{ minHeight: '100vh', background: '#000', color: '#fff', position: 'relative' }}>

                    <div className="relative z-10 w-full h-full">
                        {isRealtimePrivacyRoute ? (
                            <React.Suspense fallback={<SuspenseLoader />}>
                                <HivaRealtimePrivacy onBack={() => { window.location.hash = ''; setAuthView('landing'); }} />
                            </React.Suspense>
                        ) : isRealtimeLaunchRoute ? (
                            <React.Suspense fallback={<SuspenseLoader />}>
                                <HivaRealtimeLaunch
                                    onBack={() => { window.location.hash = ''; setAuthView('landing'); }}
                                    onLogin={() => { window.location.hash = ''; setAuthView('login'); }}
                                    onCreateAccount={() => { window.location.hash = ''; setAuthView('login'); }}
                                />
                            </React.Suspense>
                        ) : isRealtimeMarketingRoute ? (
                            <React.Suspense fallback={<SuspenseLoader />}>
                                <HivaRealtimeMarketing
                                    onBack={() => { window.location.hash = ''; setAuthView('landing'); }}
                                    onLogin={() => { window.location.hash = ''; setAuthView('login'); }}
                                    onCreateAccount={() => { window.location.hash = ''; setAuthView('login'); }}
                                />
                            </React.Suspense>
                        ) : !currentUser && !isEmbeddedRealtime ? (
                            renderAuthView()
                        ) : isEmbeddedRealtime ? (
                            <React.Suspense fallback={<SuspenseLoader />}>
                                <HivaRealtime embedded />
                            </React.Suspense>
                        ) : isRealtimeGuideRoute ? (
                            <React.Suspense fallback={<SuspenseLoader />}>
                                <HivaRealtimeGuide
                                    onBack={() => { window.location.hash = '#/realtime'; }}
                                    onOpenRealtime={() => { window.location.hash = '#/realtime'; }}
                                    onLogout={handleLogout}
                                />
                            </React.Suspense>
                        ) : isRealtimeInstallRoute ? (
                            <React.Suspense fallback={<SuspenseLoader />}>
                                <HivaRealtimeInstallations onBack={() => { window.location.hash = '#/realtime'; }} />
                            </React.Suspense>
                        ) : isRealtimeAccountRoute ? (
                            <React.Suspense fallback={<SuspenseLoader />}>
                                <HivaRealtimeAccount onBack={() => { window.location.hash = '#/realtime'; }} />
                            </React.Suspense>
                        ) : isRealtimeTestRoute ? (
                            <React.Suspense fallback={<SuspenseLoader />}>
                                <HivaRealtimeTestStore onBack={() => { window.location.hash = '#/realtime'; }} />
                            </React.Suspense>
                        ) : isAccountRoute ? (
                            <React.Suspense fallback={<SuspenseLoader />}>
                                <HivaRealtimeAccount onBack={() => { window.location.hash = ''; }} />
                            </React.Suspense>
                        ) : isRealtimeRoute ? (
                            <React.Suspense fallback={<SuspenseLoader />}>
                                <HivaRealtime
                                    onLogout={handleLogout}
                                />
                            </React.Suspense>
                        ) : routeHash.startsWith('#/studio') ? (
                            <React.Suspense fallback={<SuspenseLoader />}>
                                <EditorialStudio
                                    currentUser={currentUser}
                                    onLogout={handleLogout}
                                    onBackToPanels={() => { window.location.hash = ''; }}
                                    isDemo={isDemoSession}
                                />
                            </React.Suspense>
                        ) : (
                            <React.Suspense fallback={<SuspenseLoader />}>
                                <HivaHome
                                    onRealtime={() => { window.location.hash = '#/realtime'; }}
                                    onStudio={() => { window.location.hash = '#/studio'; }}
                                    onAccount={() => { window.location.hash = '#/conta'; }}
                                    onLogout={handleLogout}
                                />
                            </React.Suspense>
                        )}
                    </div>

                    <OnboardingModal
                        isOpen={isOnboardingOpen}
                        onClose={() => {
                            setIsOnboardingOpen(false);
                            localStorage.setItem('hiva_onboarding_seen', 'true');
                        }}
                        onStartTour={() => {
                            setIsOnboardingOpen(false);
                            localStorage.setItem('hiva_onboarding_seen', 'true');
                        }}
                        userCredits={currentUser?.credits || 0}
                    />
                </div>
            </ToastProvider>
        </ErrorBoundary>
    );
};

export default App;
